import { collection, addDoc, getDoc, onSnapshot } from "firebase/firestore";
import Constants from '../constants/Constants';

/**
 * Translate to Text Utility
 * 
 * This module handles text translation by writing to Firestore and reading back
 * the translated result. It uses a translations collection where a backend service
 * processes the translation and updates the document with the result.
 * 
 * Workflow:
 * 1. Write document to 'translations' collection with input text
 * 2. Wait for backend service to process and add translation
 * 3. Read back the translated text from the same document
 * 4. Return the English translation
 */

/**
 * Translate text using Firestore-based translation service
 * @param {string} inputString - The text to translate
 * @param {Object} db - Firestore instance
 * @param {number} maxRetries - Maximum number of retry attempts (default: 5)
 * @param {number} retryDelay - Delay between retries in milliseconds (default: 2000)
 * @param {string} fromLanguage - Source language code (default: Constants.LANGUAGES.KANNADA)
 * @param {string} toLanguage - Target language code (default: Constants.LANGUAGES.ENGLISH)
 * @returns {Promise<string>} - The translated text
 * @throws {Error} - If translation fails or times out
 */
export async function translateToText(inputString, db, maxRetries = 5, retryDelay = 2000, fromLanguage = Constants.LANGUAGES.KANNADA, toLanguage = Constants.LANGUAGES.ENGLISH) {
  if (!inputString || typeof inputString !== 'string') {
    throw new Error('Invalid input string provided for translation');
  }

  const trimmedInput = inputString.trim();
  if (!trimmedInput) {
    return '';
  }

  try {
    // Step 1: Create document in translations collection
    const docRef = await createTranslationDocument(trimmedInput, db, fromLanguage, toLanguage);
    // Step 2: Wait for translation to be processed with retries
    const translatedText = await waitForTranslationWithRetries(docRef, db, maxRetries, retryDelay, toLanguage);
    if (!translatedText) {
      throw new Error('Translation service did not provide a result after multiple attempts');
    }
    
    return translatedText;
    
  } catch (error) {
    throw new Error(`Translation failed: ${error.message}`);
  }
}

/**
 * Translate multiple texts in batch for better performance
 * @param {Array<string>} inputStrings - Array of texts to translate
 * @param {Object} db - Firestore instance
 * @param {number} maxRetries - Maximum number of retry attempts per text
 * @param {number} retryDelay - Delay between retries in milliseconds
 * @param {string} fromLanguage - Source language code (default: Constants.LANGUAGES.KANNADA)
 * @param {string} toLanguage - Target language code (default: Constants.LANGUAGES.ENGLISH)
 * @returns {Promise<Array<string>>} - Array of translated texts
 * @throws {Error} - If batch translation fails
 */
export async function translateBatchToText(inputStrings, db, maxRetries = 5, retryDelay = 2000, fromLanguage = Constants.LANGUAGES.KANNADA, toLanguage = Constants.LANGUAGES.ENGLISH) {
  if (!Array.isArray(inputStrings)) {
    throw new Error('Input must be an array of strings');
  }

  if (inputStrings.length === 0) {
    return [];
  }

  try {
    // Create translation promises for all non-empty strings
    const translationPromises = inputStrings.map(async (inputString, index) => { // eslint-disable-line no-unused-vars
      const trimmedInput = inputString?.trim();
      if (!trimmedInput) {
        return '';
      }
      
      try {
        return await translateToText(trimmedInput, db, maxRetries, retryDelay, fromLanguage, toLanguage);
      } catch (error) {
        return `[Translation Error: ${trimmedInput}]`;
      }
    });
    
    // Wait for all translations to complete
    const results = await Promise.all(translationPromises);
    return results;
    
  } catch (error) {
    throw new Error(`Batch translation failed: ${error.message}`);
  }
}

/**
 * Create a document in the translations collection
 * @param {string} inputString - The text to translate
 * @param {Object} db - Firestore instance
 * @param {string} fromLanguage - Source language code
 * @param {string} toLanguage - Target language code
 * @returns {Promise<DocumentReference>} - Reference to the created document
 */
async function createTranslationDocument(inputString, db, fromLanguage = Constants.LANGUAGES.KANNADA, toLanguage = Constants.LANGUAGES.ENGLISH) {
  try {
    const collectionName = TranslationServiceConfig.getCollectionName(fromLanguage, toLanguage);
    const translationsCollection = collection(db, collectionName);
    
    // Create document with auto-generated UID and input text
    const docData = {
      input: inputString,
      createdAt: new Date(),
      status: 'pending'
    };
    const docRef = await addDoc(translationsCollection, docData);
    return docRef;
    
  } catch (error) {
    throw new Error(`Failed to create translation document: ${error.message}`);
  }
}

/**
 * Wait for translation to be processed with retry mechanism
 * @param {DocumentReference} docRef - Reference to the translation document
 * @param {Object} db - Firestore instance
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {number} retryDelay - Delay between retries in milliseconds
 * @param {string} toLanguage - Target language code
 * @returns {Promise<string|null>} - The translated text or null if not found
 */
async function waitForTranslationWithRetries(docRef, db, maxRetries, retryDelay, toLanguage) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      
      const translatedText = await checkTranslationDocument(docRef, db, toLanguage);
      
      if (translatedText) {
        return translatedText;
      }
      
      // If no translation found and not the last attempt, wait before retrying
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
      
    } catch (error) {
      // If not the last attempt, wait before retrying
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  }
  
  return null;
}

/**
 * Check if translation document has been processed
 * @param {DocumentReference} docRef - Reference to the translation document
 * @param {Object} db - Firestore instance
 * @param {string} toLanguage - Target language code
 * @returns {Promise<string|null>} - The translated text or null if not ready
 */
async function checkTranslationDocument(docRef, db, toLanguage) {
  try {
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Translation document not found');
    }
    
    const data = docSnap.data();
    
    // Get the short language code for the target language
    const languageCode = Constants.LANGUAGE_SHORT_CODES[toLanguage] || 'en';
    
    // Check if translation is available in the expected format with dynamic language code
    if (data.translated && data.translated[languageCode]) {
      return data.translated[languageCode];
    }
    
    // Check alternative formats that might be used
    if (data.translation) {
      return data.translation;
    }
    
    if (data.result) {
      return data.result;
    }
    
    // Translation not ready yet
    return null;
    
  } catch (error) {
    throw new Error(`Failed to check translation document: ${error.message}`);
  }
}

/**
 * Wait for translation using real-time listener (alternative approach)
 * @param {DocumentReference} docRef - Reference to the translation document
 * @param {Object} db - Firestore instance
 * @param {number} timeoutMs - Maximum time to wait in milliseconds (default: 30000)
 * @param {string} toLanguage - Target language code (default: Constants.LANGUAGES.ENGLISH)
 * @returns {Promise<string>} - The translated text
 */
export async function waitForTranslationRealtime(docRef, db, timeoutMs = 30000, toLanguage = Constants.LANGUAGES.ENGLISH) {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      unsubscribe();
      reject(new Error('Translation timeout: No result received within time limit'));
    }, timeoutMs);
    
    // Get the short language code for the target language
    const languageCode = Constants.LANGUAGE_SHORT_CODES[toLanguage] || 'en';
    
    const unsubscribe = onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        
        // Check if translation is available with dynamic language code
        if (data.translated && data.translated[languageCode]) {
          clearTimeout(timeout);
          unsubscribe();
          resolve(data.translated[languageCode]);
          return;
        }
        
        // Check for error status
        if (data.status === 'error') {
          clearTimeout(timeout);
          unsubscribe();
          reject(new Error(`Translation failed: ${data.error || 'Unknown error'}`));
          return;
        }
      }
    }, (error) => {
      clearTimeout(timeout);
      reject(new Error(`Translation listener error: ${error.message}`));
    });
  });
}

/**
 * Clean up translation document after processing (optional)
 * @param {DocumentReference} docRef - Reference to the translation document
 * @param {Object} db - Firestore instance
 */
export async function cleanupTranslationDocument(docRef, db) { // eslint-disable-line no-unused-vars
  try {
    await docRef.delete();
  } catch (error) {
    // Silently fail cleanup
  }
}

/**
 * Translation service configuration
 */
export const TranslationServiceConfig = {
  // Collection name mapping imported from Constants
  get collectionNames() {
    return Constants.TRANSLATION_COLLECTIONS;
  },
  
  // Default collection name (fallback)
  defaultCollectionName: 'translations',
  
  // Default source and target languages
  get defaultFromLanguage() {
    return Constants.LANGUAGES.KANNADA;
  },
  get defaultToLanguage() {
    return Constants.LANGUAGES.ENGLISH;
  },
  
  // Default retry settings
  defaultRetries: 5,
  defaultRetryDelay: 2000, // 2 seconds
  
  // Timeout settings
  defaultTimeout: 30000, // 30 seconds
  
  // Cleanup options
  autoCleanup: false, // Set to true to automatically delete processed documents
  
  // Batch processing
  maxBatchSize: 50, // Maximum number of texts to process in parallel
  
  /**
   * Get collection name based on source and target languages
   * @param {string} fromLanguage - Source language code (e.g., 'kannada', 'english')
   * @param {string} toLanguage - Target language code (e.g., 'english', 'kannada')
   * @returns {string} - Collection name for the language pair
   */
  getCollectionName: function(fromLanguage = null, toLanguage = null) {
    const from = fromLanguage || this.defaultFromLanguage;
    const to = toLanguage || this.defaultToLanguage;
    const key = `${from}-${to}`;
    
    return this.collectionNames[key] || this.defaultCollectionName;
  }
};

/**
 * Update translation service configuration
 * @param {Object} config - New configuration options
 */
export function updateTranslationServiceConfig(config) {
  Object.assign(TranslationServiceConfig, config);
}
